import React from "react";
import katex from "katex";
import "katex/dist/katex.css";

// Detect if it's inline ($...$) or block ($$...$$)
const extractMath = (str) => {
    if (typeof str !== "string") return null;
    // block $$...$$ (allow anything inside, including $)
    let m = str.match(/^\s*\$\$([\s\S]*?)\$\$\s*$/);
    if (m) return { type: "block", content: m[1] };
    // inline $...$ (stop at next $)
    m = str.match(/^\s*\$([^$]*?)\$\s*$/);
    if (m) return { type: "inline", content: m[1] };
    return null;
};

// Strip surrounding punctuation that often appears in prose, but keep % inside
const stripSurroundingPunct = (s) =>
    s
        .trim()
        // leading punctuation like ( [ { " '
        .replace(/^[([{<'"“‘]+/, "")
        // trailing punctuation like , . ; : ! ? ) ] } " '
        .replace(/[)\]}>'"”’.,;:!?]+$/, "");

// Heuristic: treat pure numbers/currency as NOT math
// Supports: 20, 1,234, 1,234.56, -20, 100%, 2.5%, 20k, 3M, 1.2B
const isLikelyPlainNumberish = (s) => {
    const core = stripSurroundingPunct(s);
    return (
        /^[-+]?\d{1,3}(?:,\d{3})*(?:\.\d+)?(?:\s*(?:%|[kKmMbB]))?$/.test(core) ||
        /^[-+]?\d+(?:\.\d+)?(?:\s*(?:%|[kKmMbB]))?$/.test(core)
    );
};

// Consider it mathy if it has clear math cues (letters, ops, LaTeX)
const isInlineMathy = (s) => {
    const t = s.trim();
    // if starts with digit but immediately followed by a letter/underscore/^ it's mathy (e.g., 2x, 3_n)
    if (/^\d/.test(t) && /[a-zA-Z_^\\]/.test(t)) return true;
    // general cues
    return /\\|[{}_^]|[+\-*/=<>]|[a-zA-Z]/.test(t);
};

const renderInlineMathText = (text) =>
    // IMPORTANT: inline stops at next `$` so `$20, $30` becomes two tokens
    text.split(/(\$\$[\s\S]*?\$\$|\$[^$]*?\$)/g).map((part, i) => {
        const math = extractMath(part);
        if (math) {
            // Inline currency/number? leave as plain text
            if (
                math.type === "inline" &&
                isLikelyPlainNumberish(math.content) &&
                !isInlineMathy(math.content)
            ) {
                return part; // e.g., "$20", "$30", "$20," etc.
            }

            // Escape % unless already escaped (LaTeX treats % as comment)
            const safeContent = math.content.replace(/(^|[^\\])%/g, "$1\\%");

            const html = katex.renderToString(safeContent, {
                throwOnError: false,
                displayMode: math.type === "block",
            });

            if (math.type === "block") {
                return (
                    <div
                        key={`math-${i}`}
                        style={{ display: "block", textAlign: "center", margin: "0.5em 0" }}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                );
            }
            return <span key={`math-${i}`} dangerouslySetInnerHTML={{ __html: html }} />;
        }
        return part;
    });

const mapChildrenWithMath = (children) =>
    React.Children.map(children, (child, idx) => {
        if (typeof child === "string") {
            return <React.Fragment key={idx}>{renderInlineMathText(child)}</React.Fragment>;
        }
        if (Array.isArray(child)) return mapChildrenWithMath(child);
        return child;
    });

const withMath = (Tag) =>
    function Wrapped({ children, ...props }) {
        return <Tag {...props}>{mapChildrenWithMath(children)}</Tag>;
    };

// Apply to most text-containing elements
const mathWrappedComponents = (() => {
    const tags = [
        "p", "li", "span", "strong", "em", "del", "u", "i", "b",
        "blockquote",
        "h1", "h2", "h3", "h4", "h5", "h6",
        "td", "th", "caption", "figcaption",
        "label", "summary",
    ];
    return Object.fromEntries(tags.map((t) => [t, withMath(t)]));
})();

export default mathWrappedComponents;
